/* eslint-disable no-console */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import Video from 'twilio-video';
import axios from 'axios';
import GlobalLoading from '../../global/LoadingDots';
import { API_URL, COUNTRY } from '../../../environment';
import { headers } from '../../../actions/defaults';
import {
  initRoom,
  toggleCamera,
  toggleMic,
  networkColor,
  networkQuality,
  printNetworkQualityStats,
  detectHardware,
  handleTokenExpiry,
  handleReconnection,
  teardownRoom,
  getIdentityRole,
  getIdentitySegments,
  handleConnectionError,
} from '../twilioHelpers';

const INITIAL_STATE = {
  cameraOpen: true,
  audioOpen: true,
  participantIsVoice: false,
  networkQualityLevel: '',
  hardwareError: '',
  connectionError: '',
  connectionErrorCode: '',
  clientPresent: false,
  partnerPresent: false,
  coupleTogether: false,
  twilioVoiceToken: '',
  showYouAreNowConnectedMessage: false,
  isReconnecting: false,
  connectionLost: false,
  localAudioTrackId: 'initial',
  localVideoTrackId: 'initial',
};

function TwilioVideo(props) {
  const { channel, appointment, intl, sessionType } = props;
  const { formatMessage } = intl;

  const [state, setReactState] = useState(INITIAL_STATE);
  const pendingCallbacksRef = useRef([]);
  const prevLocalAudioTrackIdRef = useRef('initial');
  const selfRef = useRef(null);

  if (!selfRef.current) {
    selfRef.current = {
      state: INITIAL_STATE,
      props,
      channel,
      reconnectAttempts: 0,
      setState(updater, callback) {
        setReactState(prev => {
          const partial =
            typeof updater === 'function' ? updater(prev) : updater;
          return { ...prev, ...partial };
        });
        if (callback) {
          pendingCallbacksRef.current.push(callback);
        }
      },
    };
  }

  const self = selfRef.current;
  self.state = state;
  self.props = props;

  useEffect(() => {
    const cbs = pendingCallbacksRef.current.splice(0);
    cbs.forEach(cb => cb());
  });

  const fetchToken = () => {
    const { channel: roomId, userId: uid } = self.props;

    return axios
      .get(
        `${API_URL}/practice/twilio_videos/token?room_id=${roomId}&user_id=${uid}`,
        headers(),
      )
      .then(results => results.data);
  };

  const reconnectAfterTokenExpiry = () => {
    // Bounded retry guards against a loop if the backend keeps returning an
    // unusable token; reset on every successful (re)connect in connectToRoom.
    if (self.reconnectAttempts >= 3) return;
    self.reconnectAttempts += 1;

    fetchToken()
      .then(data => {
        if (data && data.token) {
          self.setState({ twilioVoiceToken: data.voice_token });
          connectToRoom(data.token);
        }
      })
      .catch(() => {});
  };

  const connectToRoom = token => {
    const {
      channel: roomName,
      sessionType: currentSessionType,
    } = self.props;

    return Video.connect(token, {
      networkQuality: {
        local: 1, // LocalParticipant's Network Quality verbosity [1 - 3]
        remote: 2, // RemoteParticipants' Network Quality verbosity [0 - 3]
      },
      audio: false,
      name: `${roomName}`,
    }).then(room => {
      self.reconnectAttempts = 0;

      // Clear any stale local video left over from a previous connection so a
      // token-expiry reconnect doesn't stack duplicate local tiles.
      const localMediaContainer = document.getElementById('local-media');
      if (localMediaContainer) localMediaContainer.innerHTML = '';

      initRoom.bind(self)(room);

      room.localParticipant.setNetworkQualityConfiguration({
        local: 2,
        remote: 1,
      });

      room.participants.forEach(participant => {
        if (currentSessionType === 'couples') {
          if (getIdentitySegments(participant.identity).length > 2) {
            self.setState({
              coupleTogether: true,
              clientPresent: true,
            });
          } else if (getIdentityRole(participant.identity) === 'guest') {
            self.setState({
              partnerPresent: true,
              coupleTogether: false,
            });
          } else if (getIdentityRole(participant.identity) === 'host') {
            self.setState({
              clientPresent: true,
            });
          }
        } else if (participant) {
          self.setState({
            clientPresent: true,
          });
        }
      });

      room.on('participantDisconnected', participant => {
        self.setState({
          room: self.state.room,
        });
        if (
          (currentSessionType === 'couples' && self.state.coupleTogether) ||
          currentSessionType !== 'couples'
        ) {
          self.setState({
            clientPresent: false,
            coupleTogether: false,
          });
        } else if (
          currentSessionType === 'couples' &&
          !self.state.coupleTogether
        ) {
          if (getIdentityRole(participant.identity) === 'guest') {
            self.setState({
              partnerPresent: false,
            });
          } else if (getIdentityRole(participant.identity) === 'host') {
            self.setState({
              clientPresent: false,
            });
          }
        }
      });

      room.on('participantConnected', participant => {
        self.setState({
          room: self.state.room,
        });
        if (
          (currentSessionType === 'couples' &&
            getIdentitySegments(participant.identity).length > 2) ||
          currentSessionType !== 'couples'
        ) {
          const isTogether = currentSessionType === 'couples';
          self.setState({
            clientPresent: true,
            coupleTogether: isTogether,
          });
        } else if (
          currentSessionType === 'couples' &&
          getIdentitySegments(participant.identity).length === 2
        ) {
          if (getIdentityRole(participant.identity) === 'guest') {
            self.setState({
              partnerPresent: true,
              coupleTogether: false,
            });
          } else if (getIdentityRole(participant.identity) === 'host') {
            self.setState({
              clientPresent: true,
              coupleTogether: false,
            });
          }
        }
      });

      printNetworkQualityStats.bind(self)(
        room.localParticipant.networkQualityLevel,
        room.localParticipant.networkQualityStats,
      );

      room.localParticipant.on(
        'networkQualityLevelChanged',
        printNetworkQualityStats.bind(self),
      );

      handleTokenExpiry(room, () => reconnectAfterTokenExpiry());

      handleReconnection(
        room,
        () => self.setState({ isReconnecting: true }),
        () => self.setState({ isReconnecting: false }),
        () => self.setState({ connectionLost: true }),
      );

      self.props.callbackForTwillio({
        toggleCamera: toggleCamera.bind(self),
        toggleMic: toggleMic.bind(self),
      });
    });
  };

  useEffect(() => {
    detectHardware.bind(self)();
    self.reconnectAttempts = 0;

    fetchToken()
      .then(data => {
        self.setState({ twilioVoiceToken: data.voice_token });
        return connectToRoom(data.token);
      })
      .catch(handleConnectionError.bind(self));

    return () => {
      teardownRoom(self.state.room);
    };
    // Match class mount/unmount token + room lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { localAudioTrackId, localVideoTrackId } = state;
    const prevLocalAudioTrackId = prevLocalAudioTrackIdRef.current;

    if (prevLocalAudioTrackId === 'initial') {
      if (prevLocalAudioTrackId !== localAudioTrackId) {
        // For video sessions, 'you are now connected' message will be displayed when valid localAudioTrackId && localVideoTrackId sent from twilioHelper.js
        if (appointment.modality === 'Video') {
          if (
            localAudioTrackId !== 'initial' &&
            localVideoTrackId !== 'initial'
          ) {
            self.setState({ showYouAreNowConnectedMessage: true }, () => {
              // 'you are now connected' message will be displayed for 3 seconds.
              setTimeout(() => {
                self.setState({ showYouAreNowConnectedMessage: false });
              }, 3000);
            });
          }
          // For voice sessions, 'you are now connected' message will be displayed when valid localAudioTrackId && localVideoTrackId sent from twilioHelper.js
        } else if (appointment.modality === 'Voice') {
          if (localAudioTrackId !== 'initial') {
            self.setState({ showYouAreNowConnectedMessage: true }, () => {
              // 'you are now connected' message will be displayed for 3 seconds.
              setTimeout(() => {
                self.setState({ showYouAreNowConnectedMessage: false });
              }, 3000);
            });
          }
        }
      }
    }

    prevLocalAudioTrackIdRef.current = localAudioTrackId;
  }, [state.localAudioTrackId, state.localVideoTrackId, appointment.modality, self]);

  const renderLoadingMessage = (clientArrived, twilioVoiceToken) => {
    const clientWillArriveSoon = (
      <FormattedMessage
        defaultMessage="The person you are waiting for will arrive soon. If you don't see them and it is after the scheduled appointment start time, message them directly in the chat box. For more information, <a>visit the Help Centre</a>"
        values={{
          a: chunks => (
            <a
              href={
                intl.locale === 'en'
                  ? 'https://inkblot.zendesk.com/hc/en-us/articles/360053062011-Troubleshooting-Cannot-connect-to-my-appointment'
                  : 'https://inkblot.zendesk.com/hc/fr-ca/articles/360053062011-Troubleshooting-Cannot-connect-to-my-appointment'
              }
            >
              {chunks}
            </a>
          ),
        }}
      />
    );

    const cameraOnOffUS = formatMessage(
      {
        defaultMessage:
          'It appears that your client either has their camera / microphone off, or is having some hardware issues. You can choose to use your unique dial in code to connect by voice: +1 833 934 2673 and use pin {twilioVoiceToken} followed by # or contact support.',
      },
      { twilioVoiceToken },
    );

    const cameraOnOffCA = formatMessage(
      {
        defaultMessage:
          'It appears that your client either has their camera / microphone off, or is having some hardware issues. You can choose to use your unique dial in code to connect by voice: +1 833 461 0480 and use pin {twilioVoiceToken} followed by # or contact support.',
      },
      { twilioVoiceToken },
    );

    const cameraOnOff = COUNTRY === 'US' ? cameraOnOffUS : cameraOnOffCA;

    return clientArrived ? cameraOnOff : clientWillArriveSoon;
  };

  const renderCouplesRemotes = () => {
    const { twilioVoiceToken, clientPresent, partnerPresent } = state;

    return (
      <div
        id="remote-couples"
        className="remote-couples"
        style={{ display: sessionType === 'couples' ? 'flex' : 'none' }}
      >
        <div id="remote-host-media" className="remote-couples-media">
          <GlobalLoading
            style={{ paddingBottom: '0px' }}
            message={renderLoadingMessage(clientPresent, twilioVoiceToken)}
          />
        </div>
        <div id="remote-guest-media" className="remote-couples-media">
          <GlobalLoading
            style={{ paddingBottom: '0px' }}
            message={renderLoadingMessage(partnerPresent, twilioVoiceToken)}
          />
        </div>
      </div>
    );
  };

  const { networkQualityLevel, twilioVoiceToken, clientPresent } = state;

  return (
    <div className="video-screen-wrapper">
      <div id="remote-media-div">{renderCouplesRemotes()}</div>
      <div id="local-media" />
      {(sessionType !== 'couples' ||
        (sessionType === 'couples' && state.coupleTogether === true)) && (
        <GlobalLoading
          style={{ paddingBottom: '0px' }}
          message={renderLoadingMessage(clientPresent, twilioVoiceToken)}
        />
      )}
      {state.showYouAreNowConnectedMessage && (
        <div className="you-are-now-connected-message-box">
          <p>{formatMessage({ defaultMessage: 'You are now connected' })}</p>
        </div>
      )}
      {state.isReconnecting && (
        <div className="reconnecting-message-box">
          <p>{formatMessage({ defaultMessage: 'Reconnecting…' })}</p>
        </div>
      )}
      {state.connectionLost && (
        <div className="reconnecting-message-box">
          <p>
            {formatMessage({
              defaultMessage:
                'You have been disconnected. Please refresh the page to rejoin.',
            })}
          </p>
        </div>
      )}
      <div
        className={
          state.hardwareError || state.connectionError
            ? 'unable-connect-message unable-connect-message--error'
            : 'unable-connect-message'
        }
      >
        <p>
          {formatMessage(
            {
              defaultMessage:
                'Unable to connect? Please refresh your page <b>here</b>',
            },
            {
              b: bold => (
                <b
                  onClick={() => window.location.reload()}
                  style={{ textDecoration: 'underline' }}
                >
                  {bold}
                </b>
              ),
            },
          )}
        </p>
        <span
          className="network-quality"
          style={{ color: networkColor(networkQualityLevel) }}
        >
          {networkQuality[networkQualityLevel]}
        </span>
        <span
          style={{ fontWeight: 'bold', display: 'block', marginTop: '20px' }}
        >
          {state.hardwareError || state.connectionError}
        </span>
      </div>
    </div>
  );
}

TwilioVideo.propTypes = {
  channel: PropTypes.string.isRequired,
  callbackForTwillio: PropTypes.func.isRequired,
  appointment: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  sessionType: PropTypes.string.isRequired,
};

export default injectIntl(TwilioVideo);
